import { getResellerIdFromRedux } from '@utils/common';
import http from 'httpConfig/http';

const API_BASE = import.meta.env.VITE_APP_API_URL;

const CHUNK_SIZE = 5 * 1024 * 1024;

type InitRes = { uploadId: string; key: string; bucket?: string };
type PartUrlRes = { url: string };

export async function initDroneFootageMultipart(params: {
    clientId: string;
    projectId: string;
    payload: {
        fileName: string; // original file name (or base name) - depends on your backend
        contentType: string;
        fileSizeInBytes: number;
        extension: string; // ".mp4"
    };
    signal?: AbortSignal;
}): Promise<InitRes> {
    const { clientId, projectId, payload, signal } = params;
    const resellerId = getResellerIdFromRedux();

    const { data } = await http.post<InitRes>(
        `${API_BASE}/${resellerId}/clients/${clientId}/projects/${projectId}/drone-footages/progress-center/files/multipart/init`,
        payload,
        { signal }
    );

    return data;
}

export async function getDroneFootagePartUrl(params: {
    clientId: string;
    projectId: string;
    key: string;
    uploadId: string;
    partNumber: number;
    signal?: AbortSignal;
}): Promise<PartUrlRes> {
    const { clientId, projectId, key, uploadId, partNumber, signal } = params;
    const resellerId = getResellerIdFromRedux();

    const { data } = await http.post<PartUrlRes>(
        `${API_BASE}/${resellerId}/clients/${clientId}/projects/${projectId}/drone-footages/progress-center/files/multipart/part-url`,
        { key, uploadId, partNumber },
        { signal }
    );

    return data;
}

export async function completeDroneFootageMultipart<T>(params: {
    clientId: string;
    projectId: string;
    payload: {
        fileName: string;
        key: string;
        uploadId: string;
        parts: Array<{ ETag: string; PartNumber: number }>;

        // as per your backend complete body
        name: string;
        location: { name: string } | any; // keep it flexible like your form
    };
    signal?: AbortSignal;
}): Promise<T> {
    const { clientId, projectId, payload, signal } = params;
    const resellerId = getResellerIdFromRedux();

    const { data } = await http.post<T>(
        `${API_BASE}/${resellerId}/clients/${clientId}/projects/${projectId}/drone-footages/progress-center/files/multipart/complete`,
        payload,
        { signal }
    );

    return data;
}

export async function abortDroneFootageMultipart(params: {
    clientId: string;
    projectId: string;
    key: string;
    uploadId: string;
    signal?: AbortSignal;
}) {
    const { clientId, projectId, key, uploadId, signal } = params;
    const resellerId = getResellerIdFromRedux();

    await http.post(
        `${API_BASE}/${resellerId}/clients/${clientId}/projects/${projectId}/drone-footages/progress-center/files/multipart/abort`,
        { key, uploadId },
        { signal }
    );
}

/**
 * Single file multipart upload for Drone Footage
 * - init
 * - upload parts via signed PUT
 * - complete (creates the drone-footage record and links key)
 * - abort best-effort on failure
 */
export async function uploadDroneFootageSingleFileMultipart<T>(params: {
    file: File;
    clientId: string;
    projectId: string;

    // fields required by complete endpoint
    name: string;
    location: { name: string } | any;

    signal?: AbortSignal;
    onProgress?: (progress: number) => void; // 0..100 for this file
}): Promise<T> {
    const { file, clientId, projectId, name, location, signal, onProgress } = params;

    const dot = file.name.lastIndexOf('.');
    const extension = dot >= 0 ? file.name.substring(dot) : '';
    const fileName = file.name;

    const init = await initDroneFootageMultipart({
        clientId,
        projectId,
        payload: {
            fileName,
            contentType: file.type,
            fileSizeInBytes: file.size,
            extension,
        },
        signal,
    });

    const { uploadId, key } = init;

    try {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const parts: Array<{ ETag: string; PartNumber: number }> = [];

        for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
            if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

            const start = (partNumber - 1) * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);

            const { url } = await getDroneFootagePartUrl({
                clientId,
                projectId,
                key,
                uploadId,
                partNumber,
                signal,
            });

            const putRes = await fetch(url, {
                method: 'PUT',
                body: chunk,
                headers: { 'Content-Type': file.type },
                signal,
            });

            if (!putRes.ok) {
                throw new Error(`Part ${partNumber} upload failed: ${putRes.status} ${putRes.statusText}`);
            }

            const etagRaw = putRes.headers.get('ETag') || putRes.headers.get('etag');
            if (!etagRaw) {
                throw new Error(`ETag missing for part ${partNumber}. Check storage CORS expose headers (ETag).`);
            }

            parts.push({ ETag: etagRaw.replace(/"/g, ''), PartNumber: partNumber });

            onProgress?.(Math.round((partNumber / totalChunks) * 100));
        }

        const complete = await completeDroneFootageMultipart<T>({
            clientId,
            projectId,
            payload: {
                fileName,
                key,
                uploadId,
                parts,
                name,
                location,
            },
            signal,
        });

        return complete;
    } catch (err) {
        // best-effort abort
        try {
            await abortDroneFootageMultipart({ clientId, projectId, key, uploadId, signal });
        } catch {
            // ignore abort failure
        }
        throw err;
    }
}
